import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeuix/styled';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { requestInterceptor } from './auth/request-interceptor';
import { provideMarkdown } from 'ngx-markdown';
import { environment } from '../environments/environment';

// FacilChat soporta modo diurno y nocturno. El modo nocturno se activa con
// la clase `.app-dark` sobre <html> (darkModeSelector). El esquema light usa
// los defaults claros de Aura para content/formField/text; el dark aplica los
// overrides de superficie oscura definidos más abajo.
const facilSurface = {
  0: '#ffffff',
  50: '#eeeef8',
  100: '#e0e0f0',
  200: '#c8c8dc',
  300: '#aaaac0',
  400: '#8e8ea8',
  500: '#6b6b85',
  600: '#48485a',
  700: '#38384a',
  800: '#2e2e38',
  900: '#25252e',
  950: '#1d1d22'
};

// Los tres colores de marca caen en 700 / 600 / 800 y hacen de base, hover y
// activo tal cual, sin inventar tonos intermedios.
const facilPrimaryLight = {
  color: '{primary.700}',
  contrastColor: '#ffffff',
  hoverColor: '{primary.600}',
  activeColor: '{primary.800}'
};

// En oscuro el 700 de marca da 1.53 de contraste contra el fondo (#1d1d22) y
// WCAG pide 3.0 para componentes de UI. Se usan stops mas claros de la MISMA
// rampa: mismo tono y saturacion, solo mas luminosidad.
const facilPrimaryDark = {
  color: '{primary.500}',
  contrastColor: '#ffffff',
  hoverColor: '{primary.400}',
  activeColor: '{primary.300}'
};

const facilHighlight = {
  background: 'rgba(162, 60, 226, 0.16)',
  focusBackground: 'rgba(162, 60, 226, 0.26)',
  color: '#e0b8fa',
  focusColor: '#cd91f3'
};

const facilHighlightLight = {
  background: 'rgba(92, 22, 135, 0.12)',
  focusBackground: 'rgba(92, 22, 135, 0.2)',
  color: '#5c1687',
  focusColor: '#451065'
};

// formField tokens default to {surface.0} (white) in light mode but
// {surface.950} (dark) in dark mode. Override light mode to use the
// same dark-oriented values so inputs are always dark.
const facilFormField = {
  background: '{surface.950}',
  disabledBackground: '{surface.700}',
  filledBackground: '{surface.800}',
  filledHoverBackground: '{surface.800}',
  filledFocusBackground: '{surface.800}',
  borderColor: '{surface.600}',
  hoverBorderColor: '{surface.500}',
  focusBorderColor: '{primary.color}',
  invalidBorderColor: '{red.300}',
  color: '{surface.0}',
  disabledColor: '{surface.400}',
  placeholderColor: '{surface.400}',
  shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)'
};

// content tokens control card, panel, dialog backgrounds and the splitter
// gutter (which uses content.borderColor). Light defaults to surface.0.
const facilContent = {
  background: '{surface.900}',
  hoverBackground: '{surface.800}',
  borderColor: '{surface.700}',
  color: '{surface.0}',
  hoverColor: '{surface.0}'
};

const facilText = {
  color: '{surface.0}',
  hoverColor: '{surface.0}',
  mutedColor: '{surface.400}',
  hoverMutedColor: '{surface.300}'
};

const FacilTheme = definePreset(Aura, {
  semantic: {
    // Rampa de marca: tono 277, saturacion ~72%. Los tres colores dados
    // (#5c1687 principal, #7a1db4 y #451065 secundarios) son 700, 600 y 800;
    // el resto se deriva variando solo la luminosidad.
    primary: {
      50: '#f9f0ff',
      100: '#edd3fd',
      200: '#e0b8fa',
      300: '#cd91f3',
      400: '#b866ea',
      500: '#a23ce2',
      600: '#7a1db4',
      700: '#5c1687',
      800: '#451065',
      900: '#330c4b',
      950: '#210830'
    },
    colorScheme: {
      light: {
        surface: facilSurface,
        primary: facilPrimaryLight,
        highlight: facilHighlightLight
      },
      dark: {
        surface: facilSurface,
        primary: facilPrimaryDark,
        highlight: facilHighlight,
        formField: facilFormField,
        content: facilContent,
        text: facilText
      }
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideMarkdown(),
    providePrimeNG({
      theme: {
        preset: FacilTheme,
        options: {
          darkModeSelector: '.app-dark'
        }
      }
    }),
    provideTranslateService({
      fallbackLang: 'es'
    }),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: `.json?v=${environment.i18nVersion}`
    }),
    MessageService,
    provideHttpClient(
      withInterceptors([requestInterceptor])
    )
  ]
};
